// src/pages/Onboarding/slides/DisplaySlide.js

import React, { useState, useEffect } from 'react';
import SlideShell from '../SlideShell'; // FIXED: Correct relative path

/**
 * DISPLAY SLIDE - REUSABLE COMPONENT
 * 
 * This is a generic slide component for displaying information with a central image/illustration.
 * Used for storytelling, explanations, confirmations, and motivational content.
 * 
 * WHEN TO USE:
 * - Introduction/welcome screens
 * - Feature explanations ("How it works")
 * - Motivational content ("Reclaim your confidence") 
 * - Processing/loading screens ("We're analyzing...")
 * - Confirmation screens
 * 
 * HOW TO USE:
 * <DisplaySlide
 *   title="Reclaim your confidence"
 *   description="Our AI finds the best hair transplant doctors..."
 *   imageSrc={importedImageVariable}
 *   imageAlt="Confident person illustration"
 *   theme="dark"
 *   currentStep={2}
 *   totalSteps={10}
 *   onNext={() => goNext()}
 *   onBack={() => goBack()}
 *   nextLabel="Continue"
 * />
 * 
 * PROPS:
 * - title: Main heading text
 * - description: Body text/paragraph
 * - imageSrc: Imported image variable (NOT string path)
 * - imageAlt: Alt text for image accessibility
 * - theme: 'light' or 'dark' for background color (default: 'light')
 * - currentStep/totalSteps: For progress bar
 * - onNext: Continue button callback
 * - onBack: Back button callback
 * - nextLabel: Continue button text (default: "Continue")
 * - showProgress: Whether to show progress bar (default: true)
 * - autoAdvanceDelay: Auto-advance after X milliseconds (optional)
 * - disabled: Disable continue button initially (default: false)
 * 
 * THEMES:
 * - Light: White background, dark text (default)
 * - Dark: Dark background, white text (like confidence slide)
 * 
 * BEHAVIOR:
 * - Can auto-advance after animation completes
 * - Can delay showing continue button for animations
 * - Responsive image sizing
 * - Accessible with proper alt text and focus management
 */

export default function DisplaySlide({
  title,
  description,
  imageSrc,
  imageAlt = "",
  imageBoxClassName,
  imageClassName,
  imageSize = 'default', // 'small', 'medium', 'default', 'large'
  // Optional: extra classes for the central content wrapper (lets a slide tweak spacing/alignment)
  contentContainerClassName = '',
  // NEW: Pass-through to SlideShell to offset the whole central section as a group
  contentWrapperClassName = '',
  // NEW: Control vertical alignment of the central section ('center' | 'start')
  contentJustify = 'center',
  theme = 'light',
  currentStep,
  totalSteps,
  onNext,
  onBack,
  nextLabel = "Continue",
  showProgress = true,
  autoAdvanceDelay = null,
  disabled = false,
  animationDirection = 'forward',
  isTransitioning = false,
  primaryNote = null,
  backgroundStyle = null, // Custom background color override (e.g., { backgroundColor: '#0472D8' })
}) {
  
  const [isButtonReady, setIsButtonReady] = useState(!autoAdvanceDelay && !disabled);
  
  // Handle auto-advance or delayed button appearance
  useEffect(() => {
    if (autoAdvanceDelay) {
      const timer = setTimeout(() => {
        onNext();
      }, autoAdvanceDelay);
      return () => clearTimeout(timer);
    }
    
    if (disabled) {
      // Enable button after short delay (for animations)
      const timer = setTimeout(() => {
        setIsButtonReady(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [autoAdvanceDelay, disabled, onNext]);
  
  // Theme styling
  const themeClasses = {
    light: {
      background: 'bg-white',
      text: 'text-mm_black',
      subtitle: 'text-gray-600'
    },
    dark: {
      background: 'bg-mm_black',
      text: 'text-white',
      subtitle: 'text-gray-300'
    }
  };
  
  // Image size presets
  const imageSizeClasses = {
    small: 'w-32 h-32 lg:w-48 lg:h-48 flex items-center justify-center',
    medium: 'w-48 h-48 lg:w-72 lg:h-72 flex items-center justify-center',
    default: 'w-64 h-64 lg:w-[420px] lg:h-[420px] flex items-center justify-center',
    large: 'w-80 h-80 lg:w-[500px] lg:h-[500px] flex items-center justify-center',
  };
  
  const currentTheme = themeClasses[theme];
  const containerImageClass = imageBoxClassName ?? (imageSizeClasses[imageSize] || imageSizeClasses.default);
  const imgClass = imageClassName ?? 'w-full h-full object-contain rounded-2xl';
  
  return (
    <div className={`min-h-screen ${backgroundStyle ? '' : currentTheme.background}`} style={backgroundStyle || undefined}>
      {/* Override SlideShell's white background for theme support */}
      <div className={backgroundStyle ? '' : currentTheme.background} style={backgroundStyle || undefined}>
        <SlideShell
          title={null} // We'll render title custom for theme support
          subtitle={null}
          currentStep={showProgress ? currentStep : undefined}
          totalSteps={showProgress ? totalSteps : undefined}
          onNext={onNext}
          onBack={onBack}
          nextLabel={nextLabel}
          disabled={!isButtonReady}
          theme={theme} // Pass theme down to SlideShell
          animationDirection={animationDirection}
          isTransitioning={isTransitioning}
          primaryNote={primaryNote}
          contentWrapperClassName={contentWrapperClassName}
          contentJustify={contentJustify}
          backgroundStyle={backgroundStyle}
        >
          {/* Custom Content Container for Theme Support */}
          <div className={`flex flex-col items-center text-center space-y-4 py-8 ${contentContainerClassName}`.trim()}>
            
            {/* Central Image/Illustration - FIXED: Now shows actual images */}
            {imageSrc && (
              <div className={containerImageClass}>
                <img
                  src={imageSrc}
                  alt={imageAlt}
                  className={imgClass}
                  onError={(e) => {
                    // Fallback to placeholder if image fails to load
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `
                      <div class="w-full h-full rounded-2xl flex items-center justify-center ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'
                      }">
                        <div class="text-sm font-medium ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }">
                          Image Failed to Load
                        </div>
                      </div>
                    `;
                  }}
                />
              </div>
            )}
            
            {/* Title */}
            {title && (
              <h1 className={`text-xl lg:text-2xl font-bold leading-tight px-2 ${currentTheme.text}`}>
                {title}
              </h1>
            )}
            
            {/* Description */}
            {description && (
              <p className={`text-sm lg:text-base leading-relaxed px-6 max-w-md lg:max-w-none lg:w-full ${currentTheme.subtitle}`}>
                {description}
              </p>
            )}
            
          </div>
        </SlideShell>
      </div>
    </div>
  );
}

/**
 * USAGE EXAMPLES:
 * 
 * // Import the image first
 * import welcomeImage from '../../images/welcome-illustration.png';
 * 
 * // Light theme welcome slide
 * <DisplaySlide
 *   title="Welcome to MatchMedic"
 *   description="We'll help you find the perfect hair transplant clinic in just a few minutes."
 *   imageSrc={welcomeImage}
 *   imageAlt="Welcome illustration"
 *   theme="light"
 *   nextLabel="Get Started"
 * />
 * 
 * // Dark theme motivational slide (like Figma example)
 * <DisplaySlide
 *   title="Reclaim your confidence"
 *   description="Our AI finds the best hair transplant doctors for you based on 1,000+ real patient cases. As a result, 95% of our patients report elevated confidence post procedure."
 *   imageSrc={confidenceImage}
 *   imageAlt="Confident person illustration"
 *   theme="dark"
 * />
 */